import React, { ReactNode } from 'react'
import { InferType } from 'yup'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Modal } from '../../../common/Modal'
import { ModalHeader } from '../../../common/Modal/ModalHeader'
import { ModalBody } from '../../../common/Modal/ModalBody'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { CreateUpdateMarketplaceItemForm } from '../CreateUpdateMarketplaceItemForm'
import { ToastType } from '../../../common/Toast/toast-type'
import { useToastNotifications } from '../../../../hooks/useToastNotification'
import { societyResourceCreateSchema } from '../../../../forms/schemas/society_resource_create'
import {
  IResourceWithFiles,
  NResources,
} from '../../../../interfaces/services/resources.interfaces'
import { createResource, patchResource } from '../../../../api/resources'
import { useCurrentSociety } from '../../../../hooks/useCurrentSociety'
import { unitQueries } from '../../../../api/units'
import { useAuth } from '../../../../hooks/useAuth'

interface CreateUpdateMarketplaceItemModalProps {
  show: boolean
  close: () => void
  children?: React.ReactNode
  resource?: IResourceWithFiles
}

export const CreateUpdateMarketplaceItemModal = ({
  resource,
  children,
  show,
  close,
}: CreateUpdateMarketplaceItemModalProps): ReactNode => {
  const { translate } = useAppTranslation()
  const { setToastNotification } = useToastNotifications()
  const { society, isLoading } = useCurrentSociety()
  const { userId: authUserId } = useAuth()
  const userId = authUserId as string
  const { data: units, isLoading: isUnitsLoading } = useQuery(
    unitQueries.getUserUnits(userId)
  )
  const userSocietyUnits = units?.filter(
    (unit) => unit.societyId === society?._id
  )

  const updateMode = !!resource

  const { mutate: mutatePatchResource, isPending: isUpdatingResource } =
    useMutation({
      mutationFn: patchResource,
      onSuccess: () => {
        close()
        setToastNotification(
          ToastType.SUCCESS,
          translate(
            'societyCreateUpdateResourceScreens.flashMessage.updateResourceSuccess'
          )
        )
      },
      onError: () => {
        setToastNotification(
          ToastType.DANGER,
          translate(
            'societyCreateUpdateResourceScreens.flashMessage.updateResourceFailure'
          )
        )
      },
    })

  const { mutate: mutateCreateResource, isPending: isCreatingResource } =
    useMutation({
      mutationFn: createResource,
      onSuccess: () => {
        close()
        setToastNotification(
          ToastType.SUCCESS,
          translate(
            'societyCreateUpdateResourceScreens.flashMessage.createResourceSuccess'
          )
        )
      },
      onError: () => {
        setToastNotification(
          ToastType.DANGER,
          translate(
            'societyCreateUpdateResourceScreens.flashMessage.createResourceFailure'
          )
        )
      },
    })
  const onSubmit = async (
    data: InferType<typeof societyResourceCreateSchema>
  ): Promise<void> => {
    if (updateMode) {
      mutatePatchResource({
        id: resource._id,
        body: data as NResources.NPatch.IRequestBody,
      })
    } else {
      mutateCreateResource(data as NResources.NCreate.IRequestBody)
    }
  }

  const onError = (): void => {
    if (updateMode) {
      setToastNotification(
        ToastType.DANGER,
        translate(
          'societyCreateUpdateResourceScreens.flashMessage.updateResourceFailure'
        )
      )
    } else {
      setToastNotification(
        ToastType.DANGER,
        translate(
          'societyCreateUpdateResourceScreens.flashMessage.createResourceFailure'
        )
      )
    }
  }

  const loading =
    isUpdatingResource || isCreatingResource || isLoading || isUnitsLoading
  if (!society) return null

  return (
    <>
      {children}
      <Modal show={show} size="xl">
        {{
          header: (
            <ModalHeader onClose={close}>
              {translate('createUpdateResourceModal.title')}
            </ModalHeader>
          ),
          body: (
            <ModalBody>
              <CreateUpdateMarketplaceItemForm
                userSocietyUnits={userSocietyUnits || []}
                society={society}
                onError={onError}
                onSubmit={onSubmit}
                onClose={close}
                loading={loading}
                resource={resource}
              />
            </ModalBody>
          ),
        }}
      </Modal>
    </>
  )
}
